import React, { useEffect } from "react"
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from "@mui/material"
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined"
import EmailIcon from "@mui/icons-material/Email"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import { Button } from "@oreve/business-ui"

interface UserProfileProps {
    userProfile: {
        icon: React.ReactNode
        label: string
        value: string | React.ReactNode
        hasButton: boolean
        eventClick?: () => void
        isPasswordSection?: boolean
        description?: string
    }[]
    clickOnPassword: boolean
    setIsPasswordPopinOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const ProfileTable: React.FC<UserProfileProps> = ({
    userProfile,
    clickOnPassword,
    setIsPasswordPopinOpen,
}) => {
    const mmsService = useMmsService()
    const profileService = mmsService.getProfileService()
    const [email, setEmail] = React.useState("")
    const {
        isLoading,
        sendRequest: resetPassword,
        status,
    } = profileService.useResetPassword(email)

    const validateModificationPassword = () => {
        const emailRow = userProfile.find((row) => row.label === "Email")
        if (emailRow && typeof emailRow.value === "string") {
            setEmail(emailRow.value || "")
        }
        resetPassword()
    }

    useEffect(() => {
        const emailRow = userProfile.find((row) => row.label === "Email")
        if (emailRow && typeof emailRow.value === "string") {
            setEmail(emailRow.value || "")
        }
    }, [userProfile])

    useEffect(() => {
        if (status == 200) {
            setIsPasswordPopinOpen(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status])

    return (
        <TableContainer component={Box} sx={{ marginTop: "1.5rem" }}>
            <Table
                sx={{ borderCollapse: "separate", borderSpacing: "0px 10px" }}
            >
                <TableBody>
                    {userProfile.map((row, index) => (
                        <TableRow
                            key={index}
                            sx={{
                                height:
                                    clickOnPassword && row.isPasswordSection
                                        ? "6.5rem"
                                        : "5rem",
                            }}
                        >
                            <TableCell
                                sx={{
                                    backgroundColor: "grey.100",
                                    borderBottomLeftRadius: "0.625rem",
                                    borderTopLeftRadius: "0.625rem",
                                    borderBottom: 0,
                                    width: "30%",
                                    position: "relative",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    {row.icon}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: "Roboto",
                                            fontSize: "1rem",
                                            fontWeight: 500,
                                            lineHeight: "1.25rem",
                                            letterSpacing: "0.009rem",
                                            textAlign: "left",
                                        }}
                                    >
                                        {row.label}
                                    </Typography>
                                </Box>
                                {clickOnPassword && row.isPasswordSection && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            position:
                                                clickOnPassword &&
                                                row.isPasswordSection
                                                    ? "absolute"
                                                    : "relative",
                                            bottom: "0.5rem",
                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontWeight: 400,
                                            lineHeight: "1.125rem",
                                            letterSpacing: "0px",
                                            textAlign: "left",
                                            whiteSpace: "nowrap",
                                            width: "100%",
                                        }}
                                    >
                                        {row.description}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell
                                sx={{
                                    backgroundColor: "grey.100",
                                    borderBottomRightRadius: "0.625rem",
                                    borderTopRightRadius: "0.625rem", // Coins arrondis
                                    width: "70%",
                                    borderBottom: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        paddingRight: "1.875rem",
                                    }}
                                >
                                    {row.value !== null ? row.value : ""}
                                    {row.hasButton &&
                                        (row.isPasswordSection &&
                                        clickOnPassword ? (
                                            <Button
                                                data-testid="envoyer-button"
                                                variant="contained"
                                                color="primary"
                                                children="Envoyer la demande de modification"
                                                onClick={
                                                    validateModificationPassword
                                                }
                                                startIcon={<EmailIcon />}
                                                loading={isLoading}
                                                sx={{
                                                    borderRadius: "100px",
                                                    textTransform: "none",
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "0",
                                                    transform:
                                                        "translateY(-50%)",
                                                    whiteSpace: "nowrap",
                                                    color: "#fff",
                                                }}
                                            />
                                        ) : (
                                            <Button
                                                data-testid="modifier-button"
                                                variant="outlined"
                                                color="primary"
                                                children="Modifier"
                                                onClick={row.eventClick}
                                                startIcon={
                                                    <BorderColorOutlinedIcon />
                                                }
                                                sx={{
                                                    borderRadius: "100px",
                                                    textTransform: "none",
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "0",
                                                    transform: "translateY(-50%)",
                                                    '&:first-of-type': {
                                                        color: "#003936",
                                                    },
                                                }}
                                            />
                                        ))}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default ProfileTable
